/* import __COLOCATED_TEMPLATE__ from './delete-token-modal.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import ajax from 'embercom/lib/ajax';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type NotificationsService from 'embercom/services/notifications-service';
import type Token from 'embercom/models/custom-authentication/token';

interface DependentAction {
  id: string;
  name: string;
}

interface DependencyResponse {
  actions?: DependentAction[];
}

interface Signature {
  Args: {
    token: Token | null;
    onClose: () => void;
    onDelete: () => void;
  };
}

export default class DeleteTokenModal extends Component<Signature> {
  @service declare appService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;

  @tracked dependentActions: DependentAction[] = [];

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    if (this.args.token) {
      taskFor(this.checkForDependents).perform(this.args.token);
    }
  }

  get app() {
    return this.appService.app;
  }

  get hasDependentActions(): boolean {
    return this.dependentActions.length > 0;
  }

  get shouldShowActionsList(): boolean {
    return this.dependentActions.length <= 10;
  }

  get dependentActionsCount(): number {
    return this.dependentActions.length;
  }

  get isCheckingDependencies(): boolean {
    return taskFor(this.checkForDependents).isRunning;
  }

  get title(): string {
    if (this.isCheckingDependencies) {
      return this.intl.t('settings.custom-authentication-tokens.delete-modal.loading');
    }

    if (this.hasDependentActions) {
      return this.intl.t('settings.custom-authentication-tokens.delete-modal.blocked-title', {
        tokenName: this.args.token?.name,
      });
    }

    return this.intl.t('settings.custom-authentication-tokens.delete-modal.confirm-title', {
      tokenName: this.args.token?.name,
    });
  }

  @action
  reloadDependencies() {
    if (this.args.token) {
      taskFor(this.checkForDependents).perform(this.args.token);
    }
  }

  @dropTask
  *checkForDependents(token: Token) {
    try {
      let response: DependencyResponse = yield ajax({
        url: `/ember/custom_authentication/tokens/${token.id}/dependencies`,
        type: 'GET',
        data: {
          app_id: this.app.id,
        },
      });

      let { actions } = response;
      this.dependentActions = actions ?? [];
    } catch (e: unknown) {
      console.error('Error checking token dependencies:', e);
      this.dependentActions = [];
      this.notificationsService.notifyResponseError(e, {
        default: this.intl.t('settings.custom-authentication-tokens.delete-modal.check-error'),
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::AppSettings::Authentication::DeleteTokenModal': typeof DeleteTokenModal;
    'new-settings/app-settings/authentication/delete-token-modal': typeof DeleteTokenModal;
  }
}
